import React from 'react';
import { Chip, CircularProgress, Typography } from '@mui/material';
import { PhioengagePatientProfilev2 } from '../types';
import { Add } from '@mui/icons-material';
import { updateTaskOwnerAssignment } from '../res';
import { LoadingButton } from '@mui/lab';
import { doc, getDoc } from 'firebase/firestore';
import { firestoreInstance } from 'config/ApiService';
import { logGoogleAnalyticsEvent } from 'utils/gaLog';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/reducers';
import { setUserActivity } from 'services/trackUserActivity';

const ListTaskOwnerComponent = ({
    item,
    callback
}: {
    item: PhioengagePatientProfilev2;
    callback?: () => void;
}) => {
    const [loading, setLoading] = React.useState(false);
    const [internalRef, setInternalRef] = React.useState<null | PhioengagePatientProfilev2>(item);
    const clinician = useSelector((state: RootState) => state.AuthReducer.user);

    const handleTaskOwnerUpdate = async (op: 'remove' | 'assign') => {
        if (!item) {
            return;
        }
        setLoading(true);
        try {
            await updateTaskOwnerAssignment({
                userId: item.id,
                region: item.region,
                operation: op
            });
            // Log the event
            const eventName = op === 'assign' ? 'task_picked_up' : 'task_unpicked_up';
            logGoogleAnalyticsEvent(eventName, {
                logged_in_user_id: clinician.id,
                customer_name: item.customer_name,
                region: item.region,
                logged_in_user_role: clinician.role,
                logged_in_username: clinician.name,
                customer_id: item.customer_id
            });

            await setUserActivity({
                patientId: item.id,
                eventName: eventName
            });
            if (callback) {
                callback();
            }
            await updateLocalRef();
        } catch (error) {
            console.error('Error updating task owner:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateLocalRef = async () => {
        const ref = doc(firestoreInstance(), 'phioengage_user_profiles', item.id);
        const snapshot = await getDoc(ref);
        if (!snapshot.exists()) {
            return;
        }
        const data = snapshot.data() as PhioengagePatientProfilev2;
        setInternalRef(data);
    };

    React.useEffect(() => {
        if (item) {
            setInternalRef(item);
        }
    }, [item]);

    if (!internalRef?.task_owner) {
        return (
            <LoadingButton
                onClick={() => handleTaskOwnerUpdate('assign')}
                color="info"
                size="small"
                loading={loading}
                disabled={loading || internalRef?.case_status === 'discharged'}
            >
                <Add />{' '}
                <Typography variant="caption" sx={{ textTransform: 'none' }}>
                    Pick Up
                </Typography>
            </LoadingButton>
        );
    }
    return (
        <Chip
            label={internalRef.task_owner}
            size="small"
            color="info"
            icon={loading ? <CircularProgress size={16} /> : <Add />}
            onDelete={async () => {
                await handleTaskOwnerUpdate('remove');
            }}
        />
    );
};

export default ListTaskOwnerComponent;
